import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, PlusCircle, Briefcase, Bot, Loader2, Users, Crown, Lock, Ticket, Clock, CreditCard, BarChart3, Radar, Mail, UserPlus, Check, Gift, Pencil, Trash2, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PastDueBanner from "../components/PastDueBanner";
import { MatchScore, MatchBreakdown } from "../components/MatchScore";

const TABS = [
  { id: "post", label: "Post a Job", icon: PlusCircle },
  { id: "jobs", label: "My Jobs", icon: Briefcase },
  { id: "copilot", label: "AI Copilot", icon: Bot },
  { id: "insights", label: "Insights", icon: BarChart3 },
  { id: "sourcing", label: "AI Sourcing", icon: Radar },
];

const SAMPLE_DESC = `We are hiring a Supply Chain Manager to lead procurement, inventory management and demand planning for our manufacturing operations. The ideal candidate has 7+ years experience, strong ERP/SAP skills, S&OP and forecasting expertise, and proven leadership. Salary $110,000-$130,000.`;

export default function EmployerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("post");
  const [jobs, setJobs] = useState([]);
  const [usage, setUsage] = useState(null);
  const [posting, setPosting] = useState(null);
  const isPro = user?.plan === "employer_pro";

  const loadJobs = async () => {
    try {
      const r = await api.get("/employer/jobs");
      setJobs(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const loadUsage = async () => {
    try {
      const r = await api.get("/employer/usage");
      setUsage(r.data);
    } catch (e) {}
  };

  const loadPosting = async () => {
    try {
      const r = await api.get("/employer/posting-status");
      setPosting(r.data);
    } catch (e) {}
  };

  useEffect(() => {
    loadJobs();
    loadUsage();
    loadPosting();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 py-8" data-testid="employer-dashboard">
      <PastDueBanner />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Recruiter Console</h1>
          <p className="text-sm text-[#4B5563]">AI screening, shortlists & copilot search</p>
        </div>
        <div className="flex items-center gap-2">
          {isPro ? (
            <div data-testid="plan-badge" className="font-mono2 text-xs uppercase text-[#002FA7] border border-[#002FA7] px-3 py-1.5 inline-flex items-center gap-1.5"><Crown size={13} /> Pro</div>
          ) : (
            <Link to="/pricing" data-testid="upgrade-badge" className="font-mono2 text-xs uppercase btn-ink px-3 py-1.5 inline-flex items-center gap-1.5"><Crown size={13} /> Upgrade</Link>
          )}
          <div className="font-mono2 text-xs uppercase text-[#4B5563] border border-[#E5E7EB] px-3 py-1.5">{jobs.length} roles</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-px bg-[#0A0A0A] border border-[#0A0A0A] mb-8">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`emp-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-white ${tab === t.id ? "tab-active" : ""}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "post" && <PostTab isPro={isPro} posting={posting} onReload={loadPosting} onCreated={() => { loadJobs(); loadPosting(); setTab("jobs"); }} />}
      {tab === "jobs" && <JobsTab jobs={jobs} onChanged={loadJobs} />}
      {tab === "copilot" && <CopilotTab isPro={isPro} usage={usage} onUsed={loadUsage} />}
      {tab === "insights" && <InsightsTab isPro={isPro} jobs={jobs} />}
      {tab === "sourcing" && <SourcingTab isPro={isPro} jobs={jobs} />}
    </div>
  );
}

function RedeemBox({ onReload }) {
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);

  const apply = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const r = await api.post("/employer/redeem-code", { code });
      toast.success(r.data.message || "Promo code applied.");
      setCode("");
      onReload();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="redeem-code-box">
      <Gift size={15} className="text-[#002FA7]" />
      <input
        data-testid="redeem-code-input"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => e.key === "Enter" && apply()}
        placeholder="Have a promo code?"
        className="border border-[#0A0A0A] px-3 py-1.5 text-sm font-mono2 uppercase tracking-wide focus:outline-none focus:ring-1 focus:ring-[#002FA7] flex-1 min-w-[160px]"
      />
      <button
        data-testid="redeem-code-btn"
        onClick={apply}
        disabled={applying || !code.trim()}
        className="btn-outline px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
      >
        {applying ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Apply
      </button>
    </div>
  );
}

function PostingGate({ isPro, posting, onReload }) {
  const [buying, setBuying] = useState("");

  const buy = async (kind) => {
    setBuying(kind);
    try {
      const r = await api.post("/employer/payg/checkout", { kind, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setBuying("");
    }
  };

  if (isPro)
    return (
      <div className="border border-[#002FA7] bg-[#002FA7]/5 p-4 flex items-center gap-2.5 text-sm" data-testid="posting-pro">
        <Crown size={16} className="text-[#002FA7]" />
        <span className="font-medium">Employer Pro — unlimited job postings included.</span>
      </div>
    );

  if (!posting) return null;

  if (posting.can_post)
    return (
      <div className="border border-[#0A0A0A] p-4 space-y-3" data-testid="posting-credits">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-sm">
            <Ticket size={16} className="text-[#10B981]" />
            <span className="font-medium">{posting.total_credits} job post{posting.total_credits === 1 ? "" : "s"} available</span>
            {posting.promo_credits > 0 && (
              <span className="font-mono2 text-xs text-[#002FA7] inline-flex items-center gap-1">
                <Gift size={12} /> {posting.promo_credits} free
              </span>
            )}
            {posting.day_pass && (
              <span className="font-mono2 text-xs text-[#D97706] inline-flex items-center gap-1">
                <Clock size={12} /> Day Pass expires {new Date(posting.day_pass.expires_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <button data-testid="buy-more-btn" onClick={() => buy("day_pass")} disabled={!!buying} className="btn-outline px-3 py-1.5 text-xs font-medium disabled:opacity-50">Buy more</button>
        </div>
        <div className="pt-3 border-t border-[#E5E7EB]"><RedeemBox onReload={onReload} /></div>
      </div>
    );

  return (
    <div className="border-2 border-dashed border-[#0A0A0A] p-6 fade-up" data-testid="posting-paywall">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard size={18} className="text-[#002FA7]" />
        <h3 className="font-display font-bold text-lg">Buy posting access to publish</h3>
      </div>
      <p className="text-sm text-[#4B5563] mb-5">Pay-as-you-go — no subscription needed. Pay by credit card; access starts instantly.</p>
      <div className="grid sm:grid-cols-2 gap-px bg-[#0A0A0A] border border-[#0A0A0A]">
        <div className="bg-white p-5 flex flex-col">
          <div className="font-mono2 text-xs uppercase text-[#D97706]">Best value</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1">Day Pass</div>
          <div className="font-display font-black text-4xl tracking-tighter text-[#002FA7] mt-2">$39</div>
          <ul className="mt-3 space-y-1 text-sm flex-1">
            <li className="flex gap-2"><span className="text-[#10B981]">✔</span>Up to 4 job posts</li>
            <li className="flex gap-2"><span className="text-[#10B981]">✔</span>Valid for 24 hours</li>
            <li className="flex gap-2"><span className="text-[#10B981]">✔</span>Full AI analysis & shortlists</li>
          </ul>
          <button data-testid="buy-day-pass-btn" onClick={() => buy("day_pass")} disabled={!!buying} className="btn-ink w-full py-2.5 text-sm font-medium mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {buying === "day_pass" ? <Loader2 size={16} className="animate-spin" /> : <Ticket size={16} />} Buy Day Pass
          </button>
        </div>
        <div className="bg-white p-5 flex flex-col">
          <div className="font-mono2 text-xs uppercase text-[#4B5563]">Single use</div>
          <div className="font-display font-black text-2xl tracking-tighter mt-1">Single Post</div>
          <div className="font-display font-black text-4xl tracking-tighter text-[#002FA7] mt-2">$20</div>
          <ul className="mt-3 space-y-1 text-sm flex-1">
            <li className="flex gap-2"><span className="text-[#10B981]">✔</span>One job post</li>
            <li className="flex gap-2"><span className="text-[#10B981]">✔</span>No expiry</li>
            <li className="flex gap-2"><span className="text-[#10B981]">✔</span>Full AI analysis & shortlists</li>
          </ul>
          <button data-testid="buy-single-post-btn" onClick={() => buy("single_post")} disabled={!!buying} className="btn-outline w-full py-2.5 text-sm font-medium mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {buying === "single_post" ? <Loader2 size={16} className="animate-spin" /> : <PlusCircle size={16} />} Buy Single Post
          </button>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
        <RedeemBox onReload={onReload} />
        <Link to="/pricing" className="text-xs text-[#002FA7] hover:underline font-mono2">Or go unlimited with Employer Pro →</Link>
      </div>
    </div>
  );
}

const WORK_MODE_OPTS = [["onsite", "On-site"], ["remote", "Remote"], ["hybrid", "Hybrid"]];
const EMP_TYPE_OPTS = [["full-time", "Full-time"], ["part-time", "Part-time"], ["contract", "Contract"]];
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "SGD", "AED"];
const EMPTY_JOB = { title: "", company: "", about_employer: "", location: "", work_mode: "onsite", employment_type: "full-time", salary_min: "", salary_max: "", salary_currency: "USD", description: "" };

function validateJob(form) {
  if (!form.title.trim()) return "Add a job title";
  if (!form.about_employer.trim() || form.about_employer.trim().length < 20) return "Add an 'About the employer' section (at least 20 characters)";
  if (!form.location.trim()) return "Add a location";
  if (!WORK_MODE_OPTS.some(([v]) => v === form.work_mode)) return "Select a work mode";
  if (!EMP_TYPE_OPTS.some(([v]) => v === form.employment_type)) return "Select an employment type";
  const min = Number(form.salary_min), max = Number(form.salary_max);
  if (!form.salary_min || !form.salary_max || Number.isNaN(min) || Number.isNaN(max)) return "Add a valid salary range";
  if (min > max) return "Minimum salary cannot exceed maximum salary";
  if (form.description.trim().length < 20) return "Add a fuller description (at least 20 characters)";
  return null;
}

function JobFields({ form, setForm }) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <>
      <Input label="Job title *" testid="job-title" value={form.title} onChange={set("title")} placeholder="Supply Chain Manager" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Company" testid="job-company" value={form.company} onChange={set("company")} placeholder="Acme Inc" />
        <Input label="Location *" testid="job-location" value={form.location} onChange={set("location")} placeholder="Toronto, Canada" />
      </div>
      <label className="block">
        <span className="font-mono2 text-xs uppercase text-[#4B5563]">About the employer *</span>
        <textarea
          data-testid="job-about-employer"
          value={form.about_employer}
          onChange={(e) => set("about_employer")(e.target.value)}
          rows={3}
          placeholder="Tell candidates about your company, mission and culture…"
          className="mt-1 w-full border border-[#0A0A0A] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Work mode *" testid="job-work-mode" value={form.work_mode} onChange={set("work_mode")} options={WORK_MODE_OPTS} />
        <Select label="Employment type *" testid="job-employment-type" value={form.employment_type} onChange={set("employment_type")} options={EMP_TYPE_OPTS} />
      </div>
      <div>
        <span className="font-mono2 text-xs uppercase text-[#4B5563]">Salary range *</span>
        <div className="mt-1 grid grid-cols-3 gap-3">
          <input data-testid="job-salary-min" type="number" value={form.salary_min} onChange={(e) => set("salary_min")(e.target.value)} placeholder="Min" className="w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <input data-testid="job-salary-max" type="number" value={form.salary_max} onChange={(e) => set("salary_max")(e.target.value)} placeholder="Max" className="w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <select data-testid="job-salary-currency" value={form.salary_currency} onChange={(e) => set("salary_currency")(e.target.value)} className="w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <label className="block">
        <span className="font-mono2 text-xs uppercase text-[#4B5563]">Description *</span>
        <textarea
          data-testid="job-description"
          value={form.description}
          onChange={(e) => set("description")(e.target.value)}
          rows={8}
          placeholder="Describe the role, responsibilities and requirements…"
          className="mt-1 w-full border border-[#0A0A0A] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
        />
      </label>
    </>
  );
}

function PostTab({ onCreated, isPro, posting, onReload }) {
  const [form, setForm] = useState(EMPTY_JOB);
  const [loading, setLoading] = useState(false);
  const blocked = !isPro && posting && !posting.can_post;

  const submit = async () => {
    const err = validateJob(form);
    if (err) return toast.error(err);
    setLoading(true);
    try {
      await api.post("/employer/jobs", {
        ...form,
        salary_min: Number(form.salary_min),
        salary_max: Number(form.salary_max),
      });
      toast.success("AI analyzed & posted your role!");
      onCreated();
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(apiErr(e)); onReload && onReload(); }
      else toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const loadSample = () => setForm({
    title: "Supply Chain Manager", company: "Acme Manufacturing",
    about_employer: "Acme Manufacturing is a leading industrial supplier with 500+ employees across North America, known for a collaborative culture and strong career growth.",
    location: "Toronto, Canada", work_mode: "hybrid", employment_type: "full-time",
    salary_min: "110000", salary_max: "130000", salary_currency: "USD", description: SAMPLE_DESC,
  });

  return (
    <div className="space-y-6 fade-up">
      <PostingGate isPro={isPro} posting={posting} onReload={onReload} />
      <div className="grid lg:grid-cols-2 gap-6">
      <div className="border border-[#0A0A0A] p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#002FA7]" />
          <h2 className="font-display font-bold text-lg">Job Description AI Analyzer</h2>
        </div>
        <JobFields form={form} setForm={setForm} />
        <div className="flex gap-3">
          <button data-testid="post-job-btn" onClick={submit} disabled={loading || blocked} title={blocked ? "Buy posting access first" : ""} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Analyzing…" : blocked ? "Buy access to post" : "Analyze & Post"}
          </button>
          <button data-testid="job-sample-btn" onClick={loadSample} className="btn-outline px-5 py-2.5 text-sm font-medium">
            Load sample
          </button>
        </div>
      </div>
      <div className="border border-[#0A0A0A] p-6 bg-[#F3F4F6]">
        <h3 className="font-display font-bold text-lg mb-3">What the AI extracts</h3>
        <p className="text-sm text-[#4B5563] mb-4">On submit, Darriker generates required & priority skills, experience, industry and education — then instantly ranks your talent pool. Salary, work mode and employer details you enter are shown to candidates as-is.</p>
        <ul className="space-y-2 text-sm">
          {["Required skills + priority must-haves", "Experience & seniority level", "Industry classification", "Auto-ranked candidate shortlist"].map((x) => (
            <li key={x} className="flex gap-2"><span className="text-[#002FA7]">✔</span>{x}</li>
          ))}
        </ul>
      </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono2 text-xs uppercase text-[#4B5563]">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono2 text-xs uppercase text-[#4B5563]">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function JobEditModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: job.title || "", company: job.company || "", about_employer: job.about_employer || "",
    location: job.location || "", work_mode: job.work_mode || "onsite",
    employment_type: job.employment_type || "full-time",
    salary_min: job.salary_min ?? "", salary_max: job.salary_max ?? "",
    salary_currency: job.salary_currency || "USD", description: job.description || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const err = validateJob(form);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      await api.put(`/employer/jobs/${job.id}`, {
        ...form,
        salary_min: Number(form.salary_min),
        salary_max: Number(form.salary_max),
      });
      toast.success("Job updated");
      onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="job-edit-modal" onClick={onClose}>
      <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-2xl my-8 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Pencil size={18} className="text-[#002FA7]" /> Edit job</h2>
          <button data-testid="job-edit-close" onClick={onClose} className="text-[#4B5563] hover:text-[#0A0A0A]"><X size={20} /></button>
        </div>
        <JobFields form={form} setForm={setForm} />
        <div className="flex gap-3 pt-2">
          <button data-testid="job-edit-save" onClick={save} disabled={saving} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button data-testid="job-edit-cancel" onClick={onClose} className="btn-outline px-5 py-2.5 text-sm font-medium">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function JobsTab({ jobs, onChanged }) {
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState("shortlist"); // shortlist | applicants
  const [appData, setAppData] = useState(null);
  const [editing, setEditing] = useState(null);   // job being edited

  const view = async (job, viewMode = mode) => {
    setActive(job.id);
    setLoading(true);
    if (viewMode === "shortlist") setData(null); else setAppData(null);
    try {
      if (viewMode === "shortlist") {
        const r = await api.get(`/employer/jobs/${job.id}/candidates`);
        setData(r.data);
      } else {
        const r = await api.get(`/employer/jobs/${job.id}/applicants`);
        setAppData(r.data);
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    const job = jobs.find((j) => j.id === active);
    if (job) view(job, newMode);
  };

  const setStatus = async (appId, status) => {
    try {
      await api.put(`/employer/applications/${appId}`, { status });
      toast.success("Status updated");
      const job = jobs.find((j) => j.id === active);
      if (job) view(job, "applicants");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This also removes its applicants and cannot be undone.`)) return;
    try {
      await api.delete(`/employer/jobs/${job.id}`);
      toast.success("Job deleted");
      if (active === job.id) { setActive(null); setData(null); setAppData(null); }
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!jobs.length)
    return <div className="border border-[#0A0A0A] p-10 text-center text-sm text-[#9CA3AF] font-mono2" data-testid="jobs-empty">No jobs yet — post one to get AI shortlists.</div>;

  return (
    <div className="grid lg:grid-cols-12 gap-6 fade-up">
      {editing && <JobEditModal job={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged && onChanged(); }} />}
      <div className="lg:col-span-4 space-y-3" data-testid="employer-jobs-list">
        {jobs.map((j) => (
          <div
            key={j.id}
            data-testid={`emp-job-${j.id}`}
            className={`border p-4 hard-card ${active === j.id ? "border-[#002FA7] border-2" : "border-[#E5E7EB]"}`}
          >
            <button onClick={() => view(j, mode)} className="w-full text-left" data-testid={`emp-job-select-${j.id}`}>
              <div className="font-display font-bold">{j.title}</div>
              <div className="text-xs text-[#4B5563]">{j.company} · {j.location}</div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] font-mono2 uppercase text-[#4B5563]">
                {j.work_mode && <span className="border border-[#E5E7EB] px-1.5 py-0.5">{j.work_mode}</span>}
                {j.employment_type && <span className="border border-[#E5E7EB] px-1.5 py-0.5">{j.employment_type}</span>}
                {(j.salary_min || j.salary_max) && <span className="border border-[#E5E7EB] px-1.5 py-0.5">{j.salary_currency || "USD"} {j.salary_min?.toLocaleString?.() || j.salary_min}–{j.salary_max?.toLocaleString?.() || j.salary_max}</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(j.priority_skills || []).slice(0, 3).map((s) => (
                  <span key={s} className="font-mono2 text-xs bg-[#002FA7] text-white px-1.5 py-0.5">{s}</span>
                ))}
              </div>
            </button>
            <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex gap-2">
              <button data-testid={`edit-job-${j.id}`} onClick={() => setEditing(j)} className="btn-outline px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Pencil size={12} /> Edit</button>
              <button data-testid={`delete-job-${j.id}`} onClick={() => del(j)} className="px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-colors"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      <div className="lg:col-span-8">
        {!active && <div className="border border-[#0A0A0A] p-10 text-center text-sm text-[#9CA3AF] font-mono2 flex flex-col items-center gap-2"><Users size={28} /> Select a role to view its AI shortlist & applicants.</div>}
        {active && (
          <div className="flex border border-[#0A0A0A] mb-4">
            <button data-testid="mode-shortlist" onClick={() => switchMode("shortlist")} className={`flex-1 py-2.5 text-sm font-medium ${mode === "shortlist" ? "tab-active" : ""}`}>AI Shortlist</button>
            <button data-testid="mode-applicants" onClick={() => switchMode("applicants")} className={`flex-1 py-2.5 text-sm font-medium ${mode === "applicants" ? "tab-active" : ""}`}>Applicants</button>
          </div>
        )}
        {loading && <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>}

        {mode === "applicants" && active && appData && (
          <div data-testid="applicants-pipeline">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">Applicants</h3>
              <span className="font-mono2 text-xs uppercase text-[#4B5563]">{appData.applicants.length} applied</span>
            </div>
            {appData.applicants.length === 0 ? (
              <div className="border border-[#E5E7EB] p-8 text-center text-sm text-[#9CA3AF] font-mono2">No applicants yet for this role.</div>
            ) : (
              <div className="space-y-3">
                {appData.applicants.map((a) => (
                  <div key={a.id} className="border border-[#E5E7EB] p-5 hard-card flex flex-wrap items-center gap-4" data-testid={`applicant-${a.id}`}>
                    <MatchScore score={a.match_score} size={50} />
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-display font-bold">{a.candidate?.name || "Candidate"}</div>
                      <div className="text-xs text-[#4B5563]">{a.candidate?.headline} · {a.candidate?.years_experience}+ yrs · {a.candidate?.location}</div>
                    </div>
                    <select
                      data-testid={`applicant-status-${a.id}`}
                      value={a.status}
                      onChange={(e) => setStatus(a.id, e.target.value)}
                      className="border border-[#0A0A0A] px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    >
                      <option value="new">New</option>
                      <option value="shortlisted">Shortlisted</option>
                      <option value="interview">Interview</option>
                      <option value="hired">Hired</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "shortlist" && data && (
          <div data-testid="candidate-shortlist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">AI Recommended Candidates</h3>
              <span className="font-mono2 text-xs uppercase text-[#4B5563]">{data.candidates.length} matched</span>
            </div>
            <div className="space-y-3">
              {data.candidates.map((c, idx) => (
                <div key={c.candidate.id} className="border border-[#E5E7EB] hard-card">
                  <div className="p-5 flex gap-4 items-center">
                    <span className="font-mono2 font-bold text-[#9CA3AF]">{String(idx + 1).padStart(2, "0")}</span>
                    <MatchScore score={c.score} size={56} />
                    <div className="flex-1">
                      <div className="font-display font-bold flex items-center gap-2">
                        {c.candidate.name}
                        {c.premium && (
                          <span data-testid={`priority-badge-${c.candidate.id}`} className="font-mono2 text-[10px] uppercase bg-[#002FA7] text-white px-1.5 py-0.5 inline-flex items-center gap-1">
                            <Crown size={10} /> Priority
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#4B5563]">{c.candidate.headline} · {c.candidate.years_experience}+ yrs · {c.candidate.location}</div>
                      <ul className="mt-1.5 space-y-0.5">
                        {c.reasons.slice(0, 2).map((r, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#002FA7]">✔</span>{r}</li>)}
                      </ul>
                    </div>
                    <button data-testid={`shortlist-breakdown-${c.candidate.id}`} onClick={() => setOpen(open === c.candidate.id ? null : c.candidate.id)} className="btn-outline px-3 py-1.5 text-xs font-medium">
                      {open === c.candidate.id ? "Hide" : "Details"}
                    </button>
                  </div>
                  {open === c.candidate.id && (
                    <div className="border-t border-[#E5E7EB] p-5 bg-[#F3F4F6]">
                      <MatchBreakdown breakdown={c.breakdown} />
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {c.candidate.skills.map((s) => <span key={s} className="font-mono2 text-xs border border-[#0A0A0A] px-2 py-0.5">{s}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CopilotTab({ isPro, usage, onUsed }) {
  const [msgs, setMsgs] = useState([{ role: "ai", text: "Ask me to find candidates. Try: \"Find candidates with pharmaceutical warehouse experience within 50 km of Toronto\"." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);
  const endRef = useRef(null);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs, loading]);

  const remaining = usage?.copilot_remaining;

  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input;
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/employer/copilot", { query: q });
      setMsgs((m) => [...m, { role: "ai", text: r.data.summary, results: r.data.results, count: r.data.count }]);
      onUsed && onUsed();
    } catch (e) {
      if (e?.response?.status === 402) {
        setLocked(true);
      } else {
        toast.error(apiErr(e));
      }
    } finally {
      setLoading(false);
    }
  };

  if (locked || (!isPro && remaining === 0))
    return (
      <div className="border-2 border-dashed border-[#0A0A0A] p-12 text-center fade-up" data-testid="copilot-locked">
        <Lock size={32} className="text-[#002FA7] mx-auto mb-4" />
        <h2 className="font-display font-black text-2xl tracking-tighter">Free Copilot searches used up</h2>
        <p className="text-sm text-[#4B5563] max-w-md mx-auto mt-2">
          Upgrade to Employer Recruiter Pro for unlimited AI Copilot searches, unlimited matching and interview automation — $299/month.
        </p>
        <Link to="/pricing" data-testid="copilot-upgrade-cta" className="btn-ink px-6 py-3 font-medium inline-block mt-6">Upgrade to Pro</Link>
      </div>
    );

  return (
    <div className="space-y-3 fade-up">
      {!isPro && (
        <div data-testid="copilot-usage" className="border border-[#E5E7EB] px-4 py-2 font-mono2 text-xs text-[#4B5563] flex items-center justify-between">
          <span>Free plan · {remaining ?? "—"} of {usage?.copilot_limit ?? 3} searches left</span>
          <Link to="/pricing" className="text-[#002FA7] hover:underline">Go unlimited →</Link>
        </div>
      )}
      <div className="border border-[#0A0A0A] flex flex-col h-[58vh]" data-testid="copilot-chat">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {msgs.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              <div className={`max-w-[85%] ${m.role === "user" ? "bg-[#F3F4F6] px-4 py-2.5" : "border border-[#0A0A0A] px-4 py-3"}`}>
                {m.role === "ai" && <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-1">Recruiter Copilot</div>}
                <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                {typeof m.count === "number" && (
                  <div className="mt-1 font-mono2 text-xs text-[#4B5563]">{m.count} candidates matched filters</div>
                )}
                {m.results?.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {m.results.slice(0, 5).map((r) => (
                      <div key={r.candidate.id} className="flex items-center justify-between gap-3 text-sm border-t border-[#E5E7EB] pt-1.5">
                        <span className="flex items-center gap-1.5">
                          {r.premium && <Crown size={12} className="text-[#002FA7]" />}
                          {r.candidate.name} — {r.candidate.headline}
                        </span>
                        <span className="font-mono2 font-bold text-[#002FA7]">{r.score}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && <div className="font-mono2 text-xs text-[#9CA3AF] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> searching talent pool…</div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#0A0A0A] p-3 flex gap-2">
          <input
            data-testid="copilot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Describe the candidate you need…"
            className="flex-1 border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
          <button data-testid="copilot-send" onClick={send} disabled={loading} className="btn-ink px-4 disabled:opacity-50"><Send size={18} /></button>
        </div>
      </div>
    </div>
  );
}

function ProLock({ feature }) {
  return (
    <div className="border-2 border-dashed border-[#0A0A0A] p-12 text-center fade-up" data-testid="pro-locked">
      <Lock size={32} className="text-[#002FA7] mx-auto mb-4" />
      <h2 className="font-display font-black text-2xl tracking-tighter">{feature} is an Employer Pro feature</h2>
      <p className="text-sm text-[#4B5563] max-w-md mx-auto mt-2">Upgrade to Employer Recruiter Pro for predictive hiring analytics, retention insights and AI sourcing — $299/month.</p>
      <Link to="/pricing" data-testid="pro-upgrade-cta" className="btn-ink px-6 py-3 font-medium inline-block mt-6">Upgrade to Pro</Link>
    </div>
  );
}

function JobPicker({ jobs, active, onPick }) {
  if (!jobs.length) return <span className="text-sm text-[#9CA3AF] font-mono2">Post a role first to use this tool.</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {jobs.map((j) => (
        <button key={j.id} data-testid={`pick-job-${j.id}`} onClick={() => onPick(j.id)} className={`border px-4 py-2 text-sm font-medium ${active === j.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A]"}`}>{j.title}</button>
      ))}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white p-4 text-center">
      <div className="font-mono2 text-xs uppercase text-[#4B5563]">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tighter mt-1 ${accent ? "text-[#002FA7]" : ""}`}>{value}</div>
    </div>
  );
}

function Loading({ text }) {
  return <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {text}</div>;
}

function Block({ title, children }) {
  return (
    <div className="border border-[#0A0A0A] p-5">
      <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-2">{title}</div>
      {children}
    </div>
  );
}

const FUNNEL_STEPS = [["new", "New"], ["shortlisted", "Shortlisted"], ["interview", "Interview"], ["hired", "Hired"], ["rejected", "Rejected"]];
const RISK_CLS = { Low: "text-[#10B981]", Medium: "text-[#D97706]", High: "text-[#FF3B30]" };
const SIGNAL_CLS = { Strong: "text-[#10B981]", Moderate: "text-[#D97706]", Weak: "text-[#FF3B30]" };

function InsightsTab({ isPro, jobs }) {
  const [jobId, setJobId] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState({});
  const [loadingIns, setLoadingIns] = useState("");

  if (!isPro) return <ProLock feature="Predictive Hiring Analytics" />;

  const run = async (id) => {
    setJobId(id); setLoading(true); setAnalytics(null); setApplicants([]); setInsights({});
    try {
      const [a, ap] = await Promise.all([
        api.get(`/employer/jobs/${id}/analytics`),
        api.get(`/employer/jobs/${id}/applicants`),
      ]);
      setAnalytics(a.data); setApplicants(ap.data.applicants || []);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const predict = async (appId) => {
    setLoadingIns(appId);
    try { const r = await api.get(`/employer/applications/${appId}/insights`); setInsights((p) => ({ ...p, [appId]: r.data })); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoadingIns(""); }
  };

  return (
    <div className="fade-up" data-testid="insights-tab">
      <div className="border border-[#0A0A0A] p-6 mb-6">
        <h2 className="font-display font-bold text-lg mb-1">Predictive Hiring Analytics</h2>
        <p className="text-sm text-[#4B5563] mb-4">Pick a role to see pipeline velocity, time-to-fill and per-candidate hire & retention predictions.</p>
        <JobPicker jobs={jobs} active={jobId} onPick={run} />
      </div>

      {loading && <Loading text="Crunching analytics…" />}

      {analytics && (
        <div className="space-y-6" data-testid="analytics-result">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#0A0A0A] border border-[#0A0A0A]">
            <Stat label="Applicants" value={analytics.funnel.total} />
            <Stat label="Avg match" value={`${analytics.avg_match}%`} accent />
            <Stat label="Est. time-to-fill" value={`${analytics.time_to_fill_days}d`} />
            <Stat label="Fill confidence" value={analytics.fill_confidence} />
          </div>

          <div className="border border-[#0A0A0A] p-5">
            <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-3">Pipeline funnel</div>
            <div className="space-y-2">
              {FUNNEL_STEPS.map(([k, label]) => {
                const v = analytics.funnel[k] || 0;
                const pct = analytics.funnel.total ? Math.round((v / analytics.funnel.total) * 100) : 0;
                return (
                  <div key={k} className="flex items-center gap-3">
                    <span className="w-24 text-sm">{label}</span>
                    <div className="flex-1 h-5 bg-[#F3F4F6] relative"><div className="h-5 bg-[#002FA7]" style={{ width: `${pct}%` }} /></div>
                    <span className="font-mono2 text-sm w-8 text-right">{v}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {analytics.summary && <div className="border-l-2 border-[#002FA7] pl-4"><p className="text-sm">{analytics.summary}</p></div>}
          {analytics.recommendations?.length > 0 && (
            <Block title="Recommendations to fill faster">
              <ul className="space-y-1">{analytics.recommendations.map((r, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#002FA7]">›</span>{r}</li>)}</ul>
            </Block>
          )}

          <div>
            <div className="font-mono2 text-xs uppercase text-[#4B5563] mb-2">Per-applicant predictions <span className="text-[#9CA3AF]">· AI estimate</span></div>
            {applicants.length === 0 ? (
              <div className="border border-[#E5E7EB] p-6 text-center text-sm text-[#9CA3AF] font-mono2">No applicants yet for this role.</div>
            ) : (
              <div className="space-y-3">
                {applicants.map((a) => (
                  <div key={a.id} className="border border-[#E5E7EB] p-4" data-testid={`insight-applicant-${a.id}`}>
                    <div className="flex flex-wrap items-center gap-4">
                      <MatchScore score={a.match_score} size={46} />
                      <div className="flex-1 min-w-[140px]">
                        <div className="font-display font-bold">{a.candidate?.name || "Candidate"}</div>
                        <div className="text-xs text-[#4B5563]">{a.candidate?.headline}</div>
                      </div>
                      <button data-testid={`predict-btn-${a.id}`} onClick={() => predict(a.id)} disabled={loadingIns === a.id} className="btn-outline px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        {loadingIns === a.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {insights[a.id] ? "Re-run" : "Predict"}
                      </button>
                    </div>
                    {insights[a.id] && (
                      <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid={`insight-result-${a.id}`}>
                        <div className="border border-[#E5E7EB] p-3">
                          <div className="flex items-center justify-between"><span className="font-mono2 text-xs uppercase text-[#4B5563]">Hire likelihood</span><span className={`font-mono2 text-xs uppercase ${SIGNAL_CLS[insights[a.id].hire_signal] || ""}`}>{insights[a.id].hire_signal}</span></div>
                          <div className="font-display font-black text-3xl text-[#002FA7] mt-1">{insights[a.id].hire_likelihood}%</div>
                          <ul className="mt-2 space-y-0.5">{(insights[a.id].hire_factors || []).map((f, i) => <li key={i} className="text-xs text-[#4B5563] flex gap-1.5"><span className="text-[#002FA7]">·</span>{f}</li>)}</ul>
                        </div>
                        <div className="border border-[#E5E7EB] p-3">
                          <div className="flex items-center justify-between"><span className="font-mono2 text-xs uppercase text-[#4B5563]">Retention · est.</span><span className={`font-mono2 text-xs uppercase ${RISK_CLS[insights[a.id].retention_risk] || ""}`}>{insights[a.id].retention_risk} risk</span></div>
                          <div className="font-display font-black text-3xl text-[#10B981] mt-1">{insights[a.id].retention_score}%</div>
                          <ul className="mt-2 space-y-0.5">{(insights[a.id].retention_factors || []).map((f, i) => <li key={i} className="text-xs text-[#4B5563] flex gap-1.5"><span className="text-[#10B981]">·</span>{f}</li>)}</ul>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SourcingTab({ isPro, jobs }) {
  const [jobId, setJobId] = useState("");
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outreach, setOutreach] = useState({});
  const [loadingOut, setLoadingOut] = useState("");
  const [shortlisted, setShortlisted] = useState({});
  const [shLoading, setShLoading] = useState("");

  if (!isPro) return <ProLock feature="AI Sourcing Engine" />;

  const run = async (id) => {
    setJobId(id); setLoading(true); setCands([]); setOutreach({}); setShortlisted({});
    try { const r = await api.get(`/employer/jobs/${id}/sourcing`); setCands(r.data.candidates || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const draft = async (pid) => {
    setLoadingOut(pid);
    try { const r = await api.post("/employer/sourcing/outreach", { job_id: jobId, candidate_profile_id: pid }); setOutreach((p) => ({ ...p, [pid]: r.data.message })); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoadingOut(""); }
  };

  const shortlist = async (pid) => {
    setShLoading(pid);
    try {
      const r = await api.post("/employer/sourcing/shortlist", { job_id: jobId, candidate_profile_id: pid });
      setShortlisted((p) => ({ ...p, [pid]: true }));
      toast.success(r.data.already ? "Already in your pipeline" : "Added to shortlist — check the job's Applicants");
    } catch (e) { toast.error(apiErr(e)); } finally { setShLoading(""); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Outreach copied"); };

  return (
    <div className="fade-up" data-testid="sourcing-tab">
      <div className="border border-[#0A0A0A] p-6 mb-6">
        <h2 className="font-display font-bold text-lg mb-1">AI Sourcing Engine</h2>
        <p className="text-sm text-[#4B5563] mb-4">Surface top passive candidates from the talent pool who haven't applied — then generate a personalized outreach message.</p>
        <JobPicker jobs={jobs} active={jobId} onPick={run} />
      </div>

      {loading && <Loading text="Scanning talent pool…" />}

      {cands.length > 0 && (
        <div className="space-y-3" data-testid="sourcing-results">
          {cands.map((c) => (
            <div key={c.candidate.id} className="border border-[#E5E7EB] p-5" data-testid={`source-${c.candidate.id}`}>
              <div className="flex flex-wrap items-center gap-4">
                <MatchScore score={c.score} size={52} />
                <div className="flex-1 min-w-[160px]">
                  <div className="font-display font-bold flex items-center gap-2">{c.candidate.name}{c.premium && <Crown size={13} className="text-[#002FA7]" />}</div>
                  <div className="text-xs text-[#4B5563]">{c.candidate.headline} · {c.candidate.years_experience}+ yrs · {c.candidate.location}</div>
                  <ul className="mt-1">{(c.reasons || []).slice(0, 1).map((r, i) => <li key={i} className="text-sm flex gap-2"><span className="text-[#002FA7]">✔</span>{r}</li>)}</ul>
                </div>
                <button data-testid={`outreach-btn-${c.candidate.id}`} onClick={() => draft(c.candidate.id)} disabled={loadingOut === c.candidate.id} className="btn-ink px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                  {loadingOut === c.candidate.id ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Draft outreach
                </button>
                <button data-testid={`shortlist-btn-${c.candidate.id}`} onClick={() => shortlist(c.candidate.id)} disabled={shLoading === c.candidate.id || shortlisted[c.candidate.id]} className="btn-outline px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                  {shLoading === c.candidate.id ? <Loader2 size={14} className="animate-spin" /> : shortlisted[c.candidate.id] ? <Check size={14} className="text-[#10B981]" /> : <UserPlus size={14} />} {shortlisted[c.candidate.id] ? "Shortlisted" : "Add to shortlist"}
                </button>
              </div>
              {outreach[c.candidate.id] && (
                <div className="mt-4 border border-[#0A0A0A] p-4 bg-[#F3F4F6]" data-testid={`outreach-msg-${c.candidate.id}`}>
                  <p className="text-sm whitespace-pre-wrap">{outreach[c.candidate.id]}</p>
                  <button data-testid={`copy-outreach-${c.candidate.id}`} onClick={() => copy(outreach[c.candidate.id])} className="btn-outline px-3 py-1.5 text-xs font-medium mt-3">Copy message</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && jobId && cands.length === 0 && (
        <div className="border border-[#E5E7EB] p-6 text-center text-sm text-[#9CA3AF] font-mono2">No passive candidates found for this role.</div>
      )}
    </div>
  );
}
